import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IAdminUsersService } from '../../interfaces/services/admin-users-service.interface';
import {
  AdminUser,
  CreateAdminUserRequest,
  UpdateAdminUserRequest,
} from '../../interfaces/state/entities/admin-user.interface';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AdminUsersService implements IAdminUsersService {
  private http = inject(HttpClient);
  private apiUrl = environment.scitUsersUrl;

  getUsers(): Observable<AdminUser[]> {
    return this.http.get<unknown>(this.apiUrl).pipe(map((body) => this.normalize(body)));
  }

  createUser(request: CreateAdminUserRequest): Observable<void> {
    return this.http.post<void>(this.apiUrl, {
      userId: request.userId.trim(),
      name: request.name.trim(),
      email: request.email.trim(),
      roles: request.roles,
    });
  }

  updateUser(request: UpdateAdminUserRequest): Observable<void> {
    const userId = request.userId.trim();
    const url = `${this.apiUrl.replace(/\/$/, '')}/${encodeURIComponent(userId)}`;
    return this.http.patch<void>(url, { roles: request.roles });
  }

  deleteUser(userId: string): Observable<void> {
    return this.http.delete<void>(this.apiUrl, {
      body: { userIds: [userId.trim()] },
    });
  }

  private normalize(body: unknown): AdminUser[] {
    let rawList: unknown[] = [];

    if (Array.isArray(body)) {
      rawList = body;
    } else if (body && typeof body === 'object') {
      const o = body as Record<string, unknown>;
      if (Array.isArray(o['users'])) {
        rawList = o['users'] as unknown[];
      } else if (o['userId'] != null || o['UserId'] != null) {
        rawList = [body];
      }
    }

    return rawList.map((item) => this.mapItem(item)).filter((u): u is AdminUser => u !== null);
  }

  private mapItem(item: unknown): AdminUser | null {
    if (!item || typeof item !== 'object') {
      return null;
    }
    const o = item as Record<string, unknown>;
    const userId = o['userId'] ?? o['UserId'] ?? o['id'] ?? o['Id'];
    const name = o['name'] ?? o['Name'];
    if (userId == null || name == null) {
      return null;
    }

    let roles: string[] | undefined;
    if (Array.isArray(o['roles'])) {
      roles = (o['roles'] as unknown[]).map((r) => String(r));
      if (roles.length === 0) {
        roles = undefined;
      }
    } else {
      const singleRole = o['role'] ?? o['Role'];
      if (singleRole != null && String(singleRole).trim() !== '') {
        roles = [String(singleRole)];
      }
    }

    const email = o['email'] != null ? String(o['email']) : o['Email'] != null ? String(o['Email']) : undefined;

    return {
      id: String(userId),
      name: String(name),
      ...(email ? { email } : {}),
      ...(roles ? { roles } : {}),
    };
  }
}
